// La Oficina de agentes (2026-10-02, Fase 1): datos y reglas puras de la pantalla.
// (Nació en PILOT y se movió al Radar el mismo día, a pedido de Gun.)
//
// Todo vive en kex-dashboard (migración 226 de kex-radar): agentes, corridas,
// hallazgos, preguntas, reglas y ajustes. Quién ve qué lo decide la BASE
// (RLS + oficina_nivel()): aquí solo se pinta lo que llega.
//
// "En vivo": cada cambio en esas tablas manda un toque SIN DATOS por Broadcast
// al tema `oficina`; la pantalla lo escucha y vuelve a pedir sus datos por el
// camino con permisos. Así un toque nunca filtra nada.

import type { SupabaseClient } from "@supabase/supabase-js";

// ---------------------------------------------------------------- tipos
export type Nivel = "direccion" | "gerente" | "cedis";
export type Urgencia = "alta" | "media" | "baja";
export type EstadoPersonaje = "trabajando" | "termino" | "fallo" | "pregunta" | "entregando" | "proximamente" | "descansando";
export interface Corrida {
  id: number; agente: string; inicio: string; fin: string | null; estado: "trabajando" | "termino" | "fallo";
  error: string | null; llamadas_bind: number; origen: "manual" | "cron" | "prueba";
  resumen: Resumen | null; hallazgos: number | null; preguntas: number | null;
}
export interface Resumen {
  fecha?: string; generado?: string; avisos?: string[];
  numeros?: { compras_productos: number; compras_valor: number | null; traspasos_productos: number; traspasos_valor: number | null;
    rocha_garita_productos: number; rocha_garita_urgentes: number; cero_con_venta: number; alertas_altas: number };
}
export interface Agente {
  clave: string; nombre: string; area: string; descripcion: string | null; estado: "activo" | "pausado" | "proximamente";
  horario: string | null; cron_activo: boolean; orden: number;
  personaje: { color?: string; pelo?: string; accesorio?: string };
  ultima: Pick<Corrida, "estado" | "fin" | "inicio"> & Partial<Corrida> | null;
  ultimaBuena?: Corrida | null; preguntas_abiertas: number;
}
export interface Alerta {
  id: number; agente: string; sucursal_id: number | null; sucursal_clave: string; tipo: string; urgencia: Urgencia;
  producto_codigo: string | null; producto_nombre: string | null; valor_costo: number | null; accion_sugerida: string;
  responsable: string | null; responsable_rol?: string; veces: number; primera_vez?: string; ultima_vez?: string;
}
export interface Opcion { valor: string; texto: string }
export interface ProductoPregunta { codigo: string; nombre: string; sucursal?: string; sucursales?: string[] }
export interface Pregunta {
  id: number; agente: string; para: "direccion" | "gerente"; sucursal_id: number | null; pregunta: string; detalle: string | null;
  opciones: Opcion[]; opcion_segura: string; regla_clave: string; regla_alcance: { sucursal?: string };
  contexto: { productos?: ProductoPregunta[] }; veces: number; estado: "abierta" | "contestada" | "descartada"; creada_at: string;
}
export interface Regla {
  id: number; agente: string | null; clave: string; valor: string; texto: string; activa: boolean;
  creada_por_nombre: string | null; creada_at: string; editada_por_nombre: string | null; editada_at: string | null;
}
export interface DetalleAlerta { cantidades: Record<string, unknown>; nota: string | null }
export interface DatosOficina {
  nivel: Nivel | null; agentes: Agente[]; corridas: Corrida[]; alertas: Alerta[]; preguntas: Pregunta[]; reglas: Regla[];
  ajustes: Record<string, unknown>; mostrarPesos: boolean; pesosEditadoPor: string | null;
}
// Lo que manda la Edge Function oficina-tele (sin pesos, los montos llegan en null).
export type Semaforo = Partial<Record<"rojo" | "amarillo" | "verde" | "azul" | "sin_minmax", number>>;
export interface TeleSucursal {
  clave: string; nombre: string; estado: string; productos: number; semaforo: Semaforo | null; rojos_en_cero: number | null;
  traspaso: { productos: number; cajas: number; valor_costo: number | null } | null;
  compras: { productos: number; valor_costo: number | null } | null; cero_con_venta: number | null;
  catalogo_bind: number | null; pedido_inicial_en_catalogo: number | null; pedido_inicial_productos: number | null;
}
export interface TeleDatos {
  ajustes: { mostrar_pesos: boolean; rotacion_segundos: number };
  agentes: Agente[];
  alertas: { total: number; altas: number; por_sucursal: Record<string, Record<Urgencia, number>>; top: Alerta[] };
  preguntas: { abiertas: number };
  inventario: { generado: string; sucursales: TeleSucursal[] } | null;
  ventas: { mes: string; dia: number; dias_mes: number;
    sucursales: { sucursal_id: number; meta: number | null; venta: number | null; avance: number | null }[] } | null;
}
type Rpc = <T>(fn: string, params?: Record<string, unknown>) => Promise<T>;

export const SUCURSAL_NOMBRE: Record<string, string> = {
  SLP: "San Luis (CEDIS)", GDL: "Guadalajara", CDMX: "CDMX", SALTILLO: "Saltillo",
  MAZATLAN: "Mazatlán", LEON: "León",
};

export const TIPO_TEXTO: Record<string, string> = {
  compra_proveedor_sugerida: "Comprar al proveedor",
  traspaso_sugerido: "Traspaso desde el CEDIS",
  traspaso_rocha_garita: "Traspaso Rocha → Garita",
  cero_con_venta: "En cero y se sigue vendiendo",
  existencia_negativa: "Existencia en negativo",
  minmax_incongruente: "Mín/máx mal capturado",
  sin_minmax_con_existencia: "Con mercancía y sin mín/máx",
  sobre_stock: "Arriba del máximo",
  minmax_viejo_tienda: "Mín/máx viejo en TIENDA",
  almacen_no_sumado_con_existencia: "Almacén que no se suma",
  sucursal_por_abrir: "Sucursal por abrir",
};

export const URGENCIA_TEXTO: Record<Urgencia, string> = { alta: "Urgente", media: "Media", baja: "Baja" };
const URGENCIA_ORDEN: Record<Urgencia, number> = { alta: 0, media: 1, baja: 2 };

// Cómo se lee cada cantidad del detalle ("Ver").
export const CANTIDAD_TEXTO: Record<string, string> = {
  existencia: "Hay", minimo: "Mínimo", maximo: "Máximo", en_camino: "En camino", comprometido: "Apartado",
  posicion: "Hay + en camino", existencia_garita: "Hay en la Garita", existencia_rocha: "Hay en Rocha",
  sugerido_bruto_pzas: "Faltan (solo bodega)", sugerido_neto_pzas: "Faltan (contando lo que viene)",
  proxima_eta: "Llega", clave_importacion: "Importación", faltan_para_maximo: "Faltan para el máximo",
  surtido: "Se manda por", piezas_por_caja: "Piezas por caja", cajas: "Cajas", piezas_a_mandar: "Piezas a mandar",
  almacen_origen_slp: "Sale de", existencia_origen: "Hay ahí", existencia_otro_almacen_slp: "Hay en el otro almacén",
  disponibilidad_slp: "¿Alcanza?", codigo_slp: "Código en SLP", vendidas_ultimos_dias: "Vendidas (7 días)",
  exceso_pzas: "De más", almacen: "Almacén", productos_con_existencia: "Productos con mercancía", piezas: "Piezas",
  minimo_garita: "Mín. Garita", maximo_garita: "Máx. Garita", mandar_de_rocha: "Mandar de Rocha", urgente: "Urgente",
  productos: "Productos", monin_750: "Monin 750 ml",
};
// Cantidades que no aportan a una persona (ids internos).
const CANTIDAD_OCULTA = new Set(["id_bind_slp"]);

export function filasCantidades(c: Record<string, unknown> | null | undefined): [string, string][] {
  return Object.entries(c || {})
    .filter(([k, v]) => !CANTIDAD_OCULTA.has(k) && v !== null && v !== undefined && v !== "")
    .map(([k, v]): [string, string] => [CANTIDAD_TEXTO[k] || k.replace(/_/g, " "), formatoCantidad(v)]);
}
function formatoCantidad(v: unknown): string {
  if (typeof v === "boolean") return v ? "Sí" : "No";
  if (typeof v === "number") return v.toLocaleString("es-MX", { maximumFractionDigits: 1 });
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// Pesos: si Gun los apagó, NADIE los ve (ni él, para poder enseñar la pantalla).
export function dinero(n: number | null | undefined, mostrar: boolean): string {
  if (!mostrar) return "$ •••";
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });
}
// Versión corta para tarjetas grandes y la tele: $8.6 M, $370 mil.
export function dineroCorto(n: number | null | undefined, mostrar: boolean): string {
  if (!mostrar) return "$ •••";
  if (n == null || Number.isNaN(Number(n))) return "—";
  const x = Number(n);
  if (Math.abs(x) >= 1e6) return "$" + (x / 1e6).toLocaleString("es-MX", { maximumFractionDigits: 1 }) + " M";
  if (Math.abs(x) >= 1e4) return "$" + Math.round(x / 1e3).toLocaleString("es-MX") + " mil";
  return "$" + Math.round(x).toLocaleString("es-MX");
}
export const numero = (n: number | null | undefined): string => (n == null ? "—" : Number(n).toLocaleString("es-MX", { maximumFractionDigits: 0 }));

export function haceCuanto(ts: string | null | undefined, ahora: number = Date.now()): string {
  if (!ts) return "nunca";
  const min = Math.round((ahora - new Date(ts).getTime()) / 60000);
  if (min < 1) return "hace un momento";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? "ayer" : `hace ${d} días`;
}
export function duracion(inicio: string | null | undefined, fin: string | null | undefined): string {
  if (!inicio || !fin) return "";
  const s = Math.round((new Date(fin).getTime() - new Date(inicio).getTime()) / 1000);
  return s < 90 ? `${s} s` : `${Math.round(s / 60)} min`;
}

// ---------------------------------------------------------------- estados de los personajes
export const ESTADO_TEXTO: Record<EstadoPersonaje, string> = {
  trabajando: "Trabajando…", termino: "Terminó", fallo: "Falló", pregunta: "Tiene una pregunta",
  entregando: "Entregando el reporte", proximamente: "Próximamente", descansando: "Esperando",
};
// Cuánto dura la caminata al escritorio del Director después de terminar.
export const ENTREGA_MS = 9000;

export function estadoPersonaje(agente: Pick<Agente, "estado" | "ultima" | "preguntas_abiertas">, ahora: number = Date.now()): EstadoPersonaje {
  if (agente.estado === "proximamente") return "proximamente";
  const u = agente.ultima;
  if (u?.estado === "trabajando") return "trabajando";
  if (u?.estado === "termino" && u.fin && ahora - new Date(u.fin).getTime() < ENTREGA_MS) return "entregando";
  if (u?.estado === "fallo") return "fallo";
  if ((agente.preguntas_abiertas || 0) > 0) return "pregunta";
  if (u?.estado === "termino") return "termino";
  return "descansando";
}

export function ordenarAlertas(lista: Alerta[]): Alerta[] {
  return [...lista].sort(
    (a, b) => URGENCIA_ORDEN[a.urgencia] - URGENCIA_ORDEN[b.urgencia] || Number(b.valor_costo) - Number(a.valor_costo),
  );
}

// Opciones al editar una regla desde el libro (mismas que la pregunta que la creó).
export const OPCIONES_REGLA: Record<string, [string, string][]> = {
  monin_1l_a_750: [["segun_minmax_slp", "Según mín/máx de SLP: en cero = descontinuado → 750 ml"], ["750", "Siempre el de 750 ml"], ["1L", "Siempre el de 1 L"], ["confirmar", "Marcar para confirmar"]],
  empaque_desconocido: [["no_mandar", "No mandarlos hasta confirmar la caja"], ["por_pieza", "Mandarlos por pieza"]],
  sumar_almacen: [["si", "Sí, sumarlo"], ["no", "No, dejarlo fuera"]],
};

// ---------------------------------------------------------------- carga (persona con sesión)
// Una sola llamada (api_oficina, migración 226), como todas las vistas del Radar.
// Corre con los permisos de quien llama: la base filtra por nivel y sucursal.
export async function cargarOficina(rpc: Rpc): Promise<DatosOficina> {
  const d = await rpc<Omit<DatosOficina, "mostrarPesos" | "pesosEditadoPor"> & { pesos_editado_por: string | null }>("api_oficina");
  const corridas = d.corridas || [];
  const preguntas = d.preguntas || [];
  const agentes = (d.agentes || []).map((a): Agente => {
    const mias = corridas.filter((c) => c.agente === a.clave);
    return {
      ...a,
      ultima: mias[0] || null,
      ultimaBuena: mias.find((c) => c.estado === "termino") || null,
      preguntas_abiertas: preguntas.filter((p) => p.agente === a.clave && p.estado === "abierta").length,
    };
  });
  const ajustes = d.ajustes || {};
  return {
    nivel: d.nivel,
    agentes,
    corridas,
    alertas: ordenarAlertas(d.alertas || []),
    preguntas,
    reglas: d.reglas || [],
    ajustes,
    mostrarPesos: ajustes.mostrar_pesos === true,
    pesosEditadoPor: d.pesos_editado_por || null,
  };
}

export async function detalleAlerta(rpc: Rpc, id: number): Promise<DetalleAlerta> {
  return await rpc<DetalleAlerta>("api_oficina_alerta", { p_id: id });
}

// El toque en vivo: un solo canal para todas las tablas. `alCambiar` se llama
// con un respiro (los guardados del agente mandan varios toques seguidos).
export function suscribirOficina(supa: SupabaseClient, alCambiar: () => void, espera = 700): () => void {
  let reloj: ReturnType<typeof setTimeout> | undefined;
  const canal = supa
    .channel("oficina")
    .on("broadcast", { event: "cambio" }, () => {
      clearTimeout(reloj);
      reloj = setTimeout(alCambiar, espera);
    })
    .subscribe();
  return () => {
    clearTimeout(reloj);
    supa.removeChannel(canal);
  };
}

// Errores de la base en palabras de persona.
export function porQueFallo(e: unknown): string {
  const m = String((e as { message?: string } | null)?.message || e || "");
  if (/solo Dirección/i.test(m)) return "Eso solo lo puede hacer Dirección.";
  if (/ya se contestó/i.test(m)) return "Alguien ya contestó esa pregunta.";
  if (/no es para ti|no es de tu sucursal/i.test(m)) return "Eso no le toca a tu usuario.";
  if (/Failed to fetch|NetworkError|network/i.test(m)) return "No hay conexión. Revisa el internet e intenta de nuevo.";
  return m || "Algo salió mal.";
}

// ---------------------------------------------------------------- la tele
export const TELE_LLAVE = "oficina_tele_token";
export function leerTokenTele(): string | null {
  try {
    const h = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const t = h.get("t");
    if (t) {
      localStorage.setItem(TELE_LLAVE, t);
      // El token no se queda a la vista en la barra de direcciones.
      history.replaceState(null, "", window.location.pathname);
      return t;
    }
    return localStorage.getItem(TELE_LLAVE);
  } catch {
    return null;
  }
}
